// Permission key registry — single source of truth for the Role Permissions
// matrix from Permissions 2.0 (docs/PERMISSIONS-2.0.md). Translated from the
// product spreadsheet (~/Downloads/Permissions Matrix - Role Permissions.csv).
//
// Two consumers:
//   1. Workspace creation + backfill migration — seeds role + role_perm rows.
//   2. AuthorizationService — resolves whether a role allows an action.
//
// Owner is a real system role with `yes_locked` on every key. Multiple
// users per workspace can hold it. Workspace creators are auto-assigned
// the Owner role; transfer = adding/removing user_role rows. No separate
// workspace.owner_id column — the role is the single source of truth.
//
// INVITE PERMISSIONS LIVE OUTSIDE THIS REGISTRY. They are per-(inviter_role,
// invitable_role) grants in the `role_invite_grant` join table, so custom
// roles work without registry edits. A user can invite into role T iff:
//   1. They hold some role R where role_invite_grant(R, T) exists, AND
//   2. R.hierarchy >= T.hierarchy (hard ceiling: can't invite above your tier).
// Default seeds on workspace creation:
//   admin           → can invite into {guest, limited_member, member, admin}
//   member          → can invite into {guest, limited_member, member}
//   limited_member  → no invite grants
//   guest           → no invite grants
// Custom roles get a configurable hierarchy on create (Admin picks tier) and
// no invite grants by default.

export type SystemRole = "guest" | "limited_member" | "member" | "admin" | "owner";

/**
 * Cell states for the role × permission matrix. "Locked" variants are
 * system-imposed and not editable in the role editor. "If Created" is an
 * ownership conditional resolved at check time against the actor.
 */
export type CellState =
  | "yes"
  | "no"
  | "yes_locked"
  | "no_locked"
  | "if_shared"
  | "if_created";

export type Category =
  | "collaboration"
  | "manage"
  | "workflows"
  | "create_delete"
  | "custom_fields"
  | "forms"
  | "comments"
  | "workspace";

export interface PermissionDefinition {
  /** Dot-notation permission key. */
  key: string;
  /** Group for matrix UI rendering. */
  category: Category;
  /** Human-readable label (matches CSV row). */
  label: string;
  /** Tooltip / spreadsheet description. */
  description: string;
  /** Seeded cell states. */
  defaultByRole: Record<SystemRole, CellState>;
  /** Other keys whose state this depends on. */
  interlocks?: string[];
  /**
   * Cell may take the `if_created` state — surfaces an "Only if created"
   * checkbox beneath the toggle in the role-permissions matrix UI.
   */
  supportsOnlyIfCreated?: boolean;
}

export const SYSTEM_ROLES: Record<SystemRole, true> = {
  guest: true,
  limited_member: true,
  member: true,
  admin: true,
  owner: true,
};

export const SYSTEM_ROLE_ORDER: readonly SystemRole[] = [
  "guest",
  "limited_member",
  "member",
  "admin",
  "owner",
];

/** Display names for seeded roles. Used by workspace seeder. */
export const SYSTEM_ROLE_NAMES: Record<SystemRole, string> = {
  guest: "Guest",
  limited_member: "Limited Member",
  member: "Member",
  admin: "Admin",
  owner: "Owner",
};

/**
 * Short descriptions for the system roles, shown in the permissions
 * settings page's "Roles" header strip. Owner is intentionally omitted —
 * it's a special all-access role and not surfaced alongside the editable
 * tiers.
 */
export const SYSTEM_ROLE_DESCRIPTIONS: Record<SystemRole, string> = {
  guest:
    "Outside collaborators who only see what is shared with them.",
  limited_member:
    "Teammates who work their own items but can't change Workflows or invite others.",
  member:
    "The default for most teammates. Creates, edits, and assigns work across the Workspace.",
  admin:
    "Full control over Workspace settings, members, and permissions.",
  owner: "",
};

/**
 * Hierarchy ordering for the "cannot invite above your perm level" rule.
 * Higher number = higher privilege.
 */
export const SYSTEM_ROLE_HIERARCHY: Record<SystemRole, number> = {
  guest: 1,
  limited_member: 2,
  member: 3,
  admin: 4,
  owner: 5,
};

/**
 * Whether a viewer sits at the Guest rung: every role they hold has a
 * hierarchy at or below the system Guest's. Roles are floor-never-cap, so
 * one Member-tier role lifts a viewer out of the tier; a custom role an
 * admin placed at the Guest rung keeps them in it. Holding no role at all is
 * not a Guest (an unresolved viewer must not fall into the most restricted
 * tier by accident).
 */
export function isGuestTier(
  roles: readonly { hierarchy: number }[] | null | undefined,
): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.every((role) => role.hierarchy <= SYSTEM_ROLE_HIERARCHY.guest);
}

/** All valid cell states. */
export const CELL_STATES: readonly CellState[] = [
  "yes",
  "no",
  "yes_locked",
  "no_locked",
  "if_shared",
  "if_created",
];

/** A cell state grants the action when its base verdict is positive. */
export function cellGrants(state: CellState): boolean {
  return state === "yes" || state === "yes_locked";
}

/** A cell state is editable in the role editor. */
export function cellEditable(state: CellState): boolean {
  return state !== "yes_locked" && state !== "no_locked";
}

/**
 * Collapse a locked cell state to its editable base form. Locks are a
 * system-role concept — custom roles seed from system-role templates but
 * must never carry locked cells, so seeding paths collapse before writing.
 */
export function collapseLockedState(state: CellState): CellState {
  if (state === "yes_locked") return "yes";
  if (state === "no_locked") return "no";
  return state;
}

// Full matrix translation from
//   ~/Downloads/Permissions Matrix - Role Permissions.csv
//
// Convention notes:
// - `delete.items` uses `if_created` (own-only) where the CSV shows base
//   "Yes" + sub-toggle "only_if_created=TRUE". A standalone "yes" means
//   anyone's items can be deleted.
// - `workspace.importing` is omitted — the CSV row was left blank by
//   product. Add it here once the values are defined.
export const PERMISSION_REGISTRY: readonly PermissionDefinition[] = Object.freeze([
  // Invite permissions are NOT in this registry — see header comment.
  // role_invite_grant (inviter_role_id, invitable_role_id) is the source of
  // truth for who can invite into which role.

  // ─── MANAGE ACTIONS ───────────────────────────────────────────────────
  {
    key: "manage.users",
    category: "manage",
    label: "Manage Users",
    description:
      "Add and remove members and guests, change their roles, and manage invites.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "manage.teams",
    category: "manage",
    label: "Manage Teams",
    description:
      "Create, edit, and remove teams and manage their members.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "manage.edit_workflows",
    category: "workflows",
    label: "Edit Workflows",
    description: "Edit Workflows and their Stages.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  // Admin-only by construction (every lower tier is `no_locked`), so the
  // matrix has no cell an admin could flip. `workspace` keeps it off the
  // permissions page and the New Role dialog; enforcement is unchanged.
  {
    key: "manage.stage_updates_off",
    category: "workspace",
    label: "Turn Stage Updates Off",
    description:
      "Turn a Stage's Follow ups off. Edit Workflows still allows changing how often a Stage asks for one. Admins only.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no_locked",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  // The per-Project counterpart: end a Follow up on one Project whose Stage or
  // Workflow default keeps a clock running. Unlike the Stage key it CAN be
  // granted to Members — a workspace that wants reps to opt out flips the
  // cell; the default keeps loosening a policy in Admin hands. Tightening a
  // cycle needs no permission beyond editing the Project.
  {
    key: "manage.project_updates_off",
    category: "manage",
    label: "End Project Follow ups",
    description:
      "Turn Follow ups off on a Project whose Stage or Workflow has them on by default. Anyone who can edit a Project can still shorten its cycle.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "widgets.manage",
    category: "manage",
    label: "Manage Widgets",
    description:
      "Add, configure, rearrange, and remove widgets. Boards are shared across the Workspace, so everyone else sees them read-only.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── CREATE & DELETE ACTIONS ──────────────────────────────────────────
  {
    key: "create.roles",
    category: "manage",
    label: "Create Roles",
    description:
      "Create, edit, and delete custom roles. Role editors can grant themselves anything, so this is effectively full access. Admins only.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no_locked",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "create.workflows",
    category: "workflows",
    label: "Create Workflows",
    description: "Create new Workflows.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "delete.workflows",
    category: "workflows",
    label: "Delete Workflows",
    description: "Delete Workflows.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "create.views",
    category: "workflows",
    label: "Create Views",
    description:
      "Create and edit views everyone in the Workspace can see. Without it, users can still create private views.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "create.items",
    category: "create_delete",
    label: "Create Items",
    description:
      "Create items in Workflows the user has access to.",
    defaultByRole: {
      // Guests start at `no` (editable) instead of `no_locked` —
      // admins can flip this on per-workspace if external collaborators
      // need to create items in workflows they've been shared on.
      guest: "no",
      limited_member: "yes",
      member: "yes",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "delete.items",
    category: "create_delete",
    label: "Delete Items",
    description:
      "Delete items. \"Only if created\" limits this to ones the user created.",
    supportsOnlyIfCreated: true,
    defaultByRole: {
      guest: "if_created",
      limited_member: "if_created",
      member: "if_created",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "archive.items",
    category: "create_delete",
    label: "Archive Items",
    description: "Archive items.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "yes",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── CUSTOM FIELDS ────────────────────────────────────────────────────
  {
    key: "custom_fields.manage",
    category: "custom_fields",
    label: "Manage Custom Fields",
    description:
      "Create, edit, and delete Custom Fields.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
    interlocks: ["delete.items"],
  },

  // ─── TRASH ────────────────────────────────────────────────────────────
  // Restore-from-trash, permanent-delete, and "empty trash". Workspace-
  // wide cleanup affecting many items — admin/owner only by default.
  {
    key: "trash.manage",
    category: "workspace",
    label: "Manage Trash",
    description:
      "Restore or permanently delete trashed items, and empty the trash.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── WORKSPACE INTEGRATIONS ───────────────────────────────────────────
  // Set up / manage third-party integrations (webhooks, etc.). Workspace-
  // config tier. Guest/Limited/Member default `no` (editable, not locked)
  // so an admin can grant it to a lower tier if needed.
  {
    key: "workspace.integrations",
    category: "workspace",
    label: "Workspace Integrations",
    description:
      "Set up and manage third-party integrations, such as webhooks.",
    defaultByRole: {
      guest: "no",
      limited_member: "no",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── ISSUE TYPES ──────────────────────────────────────────────────────
  // Item type templates plus their field layout. Workspace-config tier.
  {
    key: "issue_types.manage",
    category: "workspace",
    label: "Manage Item Types",
    description:
      "Create, edit, and delete item types and their field layouts.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── REASONS ──────────────────────────────────────────────────────────
  // "Outcomes" in the UI is the configurable update-reasons list (the
  // catalog of pre-canned reasons surfaced when posting an update on an
  // item). Workspace-config tier — admins and owners only by default.
  {
    key: "reasons.manage",
    category: "workspace",
    label: "Manage Outcomes",
    description:
      "Create, edit, and delete the outcomes available when posting an update.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── AUTOMATIONS ──────────────────────────────────────────────────────
  // Workflow automations are workspace-wide machinery — editing and
  // publishing them is admin/owner only by default. Read access is not gated
  // here (everyone in the workspace can view automations and run history).
  {
    key: "automations.manage",
    category: "workspace",
    label: "Manage Automations",
    description:
      "Create, edit, publish, and delete Automations.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── ISSUE SOURCES ────────────────────────────────────────────────────
  // "Sources" in the UI is the configurable lead-source list (the catalog of
  // issue sources selectable on an item). Workspace-config tier — admins and
  // owners only by default.
  //
  // The conditional cell is the "grow it, don't govern it" tier for a
  // catalog: `if_created` lets a member add a source from the picker and
  // rename or disable the ones they added, while the catalog itself (other
  // people's sources, the default, deletion) stays with the unconditional
  // grant. Reporting keys on sources, so pool-wide edits stay admin work.
  {
    key: "issue_sources.manage",
    category: "workspace",
    label: "Manage Sources",
    description:
      "Create, edit, and delete the lead sources available across the Workspace. \"Only if created\" lets users add sources and manage the ones they added.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
    supportsOnlyIfCreated: true,
  },

  // ─── FORMS ────────────────────────────────────────────────────────────
  // Filling is not gated here: it rides the Project's own edit access, since linked answers write its fields.
  {
    key: "forms.manage",
    category: "forms",
    label: "Manage Forms",
    description:
      "Create, edit, publish, share, and delete Forms, and see every submission. Anyone who can edit a Project can still fill out its Forms.",
    defaultByRole: {
      guest: "no_locked",
      limited_member: "no_locked",
      member: "no",
      admin: "yes",
      owner: "yes_locked",
    },
  },

  // ─── COMMENTS ─────────────────────────────────────────────────────────
  {
    key: "comments.edit_own",
    category: "comments",
    label: "Edit Own Comments",
    description: "Edit the user's own comments.",
    defaultByRole: {
      guest: "yes",
      limited_member: "yes",
      member: "yes",
      admin: "yes",
      owner: "yes_locked",
    },
  },
  {
    key: "comments.delete_own",
    category: "comments",
    label: "Delete Own Comments",
    description: "Delete the user's own comments.",
    defaultByRole: {
      guest: "yes",
      limited_member: "yes",
      member: "yes",
      admin: "yes",
      owner: "yes_locked",
    },
  },
]);

export const PERMISSION_BY_KEY: ReadonlyMap<string, PermissionDefinition> = new Map(
  PERMISSION_REGISTRY.map((p) => [p.key, p]),
);

/** Resolve a single (role, key) cell using registry defaults. */
export function defaultCellState(role: SystemRole, key: string): CellState | null {
  const def = PERMISSION_BY_KEY.get(key);
  if (!def) return null;
  return def.defaultByRole[role];
}

// ─── ACCESS LEVELS ──────────────────────────────────────────────────────
// Object-level access levels (workflow / item). View-level (full_edit |
// limited) is deferred along with the view entity itself.

export type ObjectAccessLevel = "full_edit" | "edit" | "comment" | "view";

export const OBJECT_ACCESS_LEVELS: Record<ObjectAccessLevel, true> = {
  full_edit: true,
  edit: true,
  comment: true,
  view: true,
};

/**
 * Per access level, which CRUD capabilities are granted at the object.
 * Source: Permissions Matrix - Access Permissions.csv (Heirarchical Access
 * Views section).
 */
export const ACCESS_LEVEL_CAPS: Record<
  ObjectAccessLevel,
  { view: boolean; comment: boolean; edit: boolean; create: boolean; delete: boolean }
> = {
  full_edit: { view: true, comment: true, edit: true, create: true, delete: true },
  edit: { view: true, comment: true, edit: true, create: true, delete: false },
  comment: { view: true, comment: true, edit: false, create: false, delete: false },
  view: { view: true, comment: false, edit: false, create: false, delete: false },
};

export function accessLevelAllows(
  level: ObjectAccessLevel,
  action: "view" | "comment" | "edit" | "create" | "delete",
): boolean {
  return ACCESS_LEVEL_CAPS[level]?.[action] === true;
}

/**
 * Numeric ranking for ObjectAccessLevel comparisons — higher grants
 * more. Each tier implies the lower ones.
 */
export const ACCESS_LEVEL_RANK: Record<ObjectAccessLevel, number> = {
  view: 1,
  comment: 2,
  edit: 3,
  full_edit: 4,
};

export function isObjectAccessLevel(level: string): level is ObjectAccessLevel {
  return level in OBJECT_ACCESS_LEVELS;
}

/** Rank of a level; 0 for unknown levels. */
export function accessLevelRank(level: ObjectAccessLevel | string): number {
  return isObjectAccessLevel(level) ? ACCESS_LEVEL_RANK[level] : 0;
}

/**
 * The most-granting level of the given non-empty list. Used when
 * multiple shares grant the same object and the max should win.
 */
export function highestAccessLevel(levels: ObjectAccessLevel[]): ObjectAccessLevel {
  return levels.reduce((acc, l) => (accessLevelRank(l) > accessLevelRank(acc) ? l : acc));
}

// ─── INVITE GRANT DEFAULTS ─────────────────────────────────────────────
// Seeded into role_invite_grant on workspace creation. Keys are inviter
// roles; values are the set of invitable target roles. Read as:
//   "By default, this inviter can invite users into these target roles."
// Hierarchy ceiling is enforced separately at check time — even if a row
// exists, an inviter with lower hierarchy than the target is rejected.

export const DEFAULT_INVITE_GRANTS: Record<SystemRole, readonly SystemRole[]> = {
  guest: [],
  limited_member: [],
  member: ["guest", "limited_member", "member"],
  admin: ["guest", "limited_member", "member", "admin"],
  owner: ["guest", "limited_member", "member", "admin", "owner"],
};

// ─── INVITE GRANT CELL STATES ──────────────────────────────────────────
// role_invite_grant carries a `state` (cell_state) per (inviter, target)
// pair, exactly like role_perm. Seeded as a full grid on workspace
// creation and on custom-role creation. Resolution rules:
//
//   1. Target outranks inviter (hierarchy ceiling)  → no_locked  (×)
//   2. Inviter is a Guest (guests never invite)      → no_locked  (×)
//   3. Inviter is Admin/Owner (always can, at tier)  → yes_locked (✓)
//   4. Pair is a DEFAULT_INVITE_GRANT                 → yes        (toggle on)
//   5. Otherwise                                      → no         (toggle off)
//
// `*_locked` states aren't editable in the matrix; `yes`/`no` are. Custom
// roles are never locked except by the hierarchy ceiling (rule 1) — they
// have no systemRole, so rules 2–4 don't apply and they default to `no`.

/**
 * Resolve the default invite-grant cell state for an (inviter, target)
 * role pair. Each arg carries the role's hierarchy tier and, for system
 * roles, its SystemRole key (null for custom roles).
 */
export function defaultInviteState(
  inviter: { hierarchy: number; systemRole: SystemRole | null },
  target: { hierarchy: number; systemRole: SystemRole | null },
): CellState {
  if (target.hierarchy > inviter.hierarchy) return "no_locked";
  if (inviter.systemRole === "guest") return "no_locked";
  if (inviter.systemRole === "admin" || inviter.systemRole === "owner") {
    return "yes_locked";
  }
  if (
    inviter.systemRole &&
    target.systemRole &&
    DEFAULT_INVITE_GRANTS[inviter.systemRole].includes(target.systemRole)
  ) {
    return "yes";
  }
  return "no";
}
